import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleCard from '../common/TrebbleCard';
import ModalHeader from '../common/ModalHeader';

/**
 * WorkspaceBillingContent displays billing management UI for workspace owners/admins.
 * Shows plan info, seat count with +/- controls, pricing, and actions for managing
 * billing through Stripe portal and cancelling the plan.
 * Rendered inside an OptionPopupView.
 *
 * @param {Object} props
 * @param {Object} props.workspace - { name, planName, seatsUsed, seatsTotal, pricePerSeat, billingCycle }
 * @param {function} props.onManageBilling - () => Promise<void> - Opens Stripe billing portal
 * @param {function} props.onUpdateSeats - (newSeatCount) => Promise<void>
 * @param {function} props.onCancel - () => Promise<void> - Cancel the team plan
 * @param {function} props.onClose - () => void
 * @param {Object} props.i18nModule - i18n module for translations
 */
const WorkspaceBillingContent = ({
    workspace,
    onManageBilling,
    onUpdateSeats,
    onCancel,
    onClose,
    i18nModule,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [seatCount, setSeatCount] = useState(workspace ? workspace.seatsTotal : 0);
    const [errorMessage, setErrorMessage] = useState(null);

    const t = (key) => {
        return window.getI18n(i18nModule, key);
    };

    const formatTemplate = (template, vars) => {
        let result = template;
        Object.keys(vars).forEach((key) => {
            result = result.replace('${' + key + '}', String(vars[key]));
        });
        return result;
    };

    const handleManageBilling = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onManageBilling();
        } catch (error) {
            const message = error instanceof Error ? error.message : t('BILLING_PORTAL_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddSeat = async () => {
        const newCount = seatCount + 1;
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onUpdateSeats(newCount);
            setSeatCount(newCount);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('SEATS_UPDATE_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveSeat = async () => {
        if (seatCount <= (workspace ? workspace.seatsUsed : 1)) {
            return;
        }
        const newCount = seatCount - 1;
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onUpdateSeats(newCount);
            setSeatCount(newCount);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('SEATS_UPDATE_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onCancel();
            setShowCancelConfirm(false);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('CANCEL_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    if (!workspace) {
        return (
            <TrebbleCard
                useEditorTheme={false}
                style={{ padding: '24px' }}
            >
                <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>
                    {t('BILLING_LOADING')}
                </div>
            </TrebbleCard>
        );
    }

    const canRemoveSeat = seatCount > (workspace.seatsUsed || 1);

    const sectionLabelStyle = {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#888',
        marginBottom: '8px',
    };

    const sectionValueStyle = {
        fontSize: '14px',
        fontWeight: 500,
    };

    return (
        <TrebbleCard
            useEditorTheme={false}
            style={{ padding: '24px' }}
        >
            <ModalHeader
                title={t('BILLING_TITLE')}
                marginBottom={24}
            />

            {/* Plan badge */}
            <div style={{ marginBottom: '20px' }}>
                <div style={sectionLabelStyle}>
                    {t('CURRENT_PLAN')}
                </div>
                <div style={{ display: 'inline-block', padding: '4px 12px', backgroundColor: '#e6f7ff', color: '#1890ff', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {workspace.planName || t('PLAN_NAME')}
                </div>
            </div>

            {/* Seats */}
            <div style={{ marginBottom: '20px' }}>
                <div style={sectionLabelStyle}>
                    {t('TEAM_SEATS')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={sectionValueStyle}>
                        {formatTemplate(t('SEATS_USED'), {
                            used: workspace.seatsUsed,
                            total: seatCount,
                        })}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <TrebbleButton
                            size="small"
                            onClick={handleRemoveSeat}
                            disabled={!canRemoveSeat || isLoading}
                            useEditorTheme={false}
                            style={{ minWidth: '32px', padding: '0 8px' }}
                        >
                            -
                        </TrebbleButton>
                        <TrebbleButton
                            size="small"
                            onClick={handleAddSeat}
                            disabled={isLoading}
                            useEditorTheme={false}
                            style={{ minWidth: '32px', padding: '0 8px' }}
                        >
                            +
                        </TrebbleButton>
                    </div>
                </div>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '24px' }}>
                <div style={sectionLabelStyle}>
                    {t('PRICING')}
                </div>
                <div style={sectionValueStyle}>
                    {formatTemplate(t('PRICE_PER_SEAT'), {
                        price: workspace.pricePerSeat || '--',
                    })}
                </div>
            </div>

            {/* Error message */}
            {errorMessage && (
                <div style={{ color: '#ff4d4f', fontSize: '13px', marginBottom: '12px' }}>
                    {errorMessage}
                </div>
            )}

            {/* Cancel confirmation */}
            {showCancelConfirm && (
                <div style={{ backgroundColor: '#fff2f0', padding: '16px', marginBottom: '16px', border: '1px solid #ffccc7' }}>
                    <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                        {t('CANCEL_CONFIRM')}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <TrebbleButton
                            size="small"
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={isLoading}
                            useEditorTheme={false}
                        >
                            {t('BACK')}
                        </TrebbleButton>
                        <TrebbleButton
                            size="small"
                            danger
                            onClick={handleCancel}
                            loading={isLoading}
                            useEditorTheme={false}
                        >
                            {t('CANCEL_PLAN')}
                        </TrebbleButton>
                    </div>
                </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    {!showCancelConfirm && (
                        <TrebbleButton
                            type="text"
                            danger
                            size="small"
                            onClick={() => setShowCancelConfirm(true)}
                            disabled={isLoading}
                            useEditorTheme={false}
                        >
                            {t('CANCEL_PLAN')}
                        </TrebbleButton>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <TrebbleButton
                        onClick={onClose}
                        useEditorTheme={false}
                        disabled={isLoading}
                    >
                        {t('CLOSE')}
                    </TrebbleButton>
                    <TrebbleButton
                        type="primary"
                        onClick={handleManageBilling}
                        loading={isLoading}
                        useEditorTheme={false}
                    >
                        {t('MANAGE_BILLING')}
                    </TrebbleButton>
                </div>
            </div>
        </TrebbleCard>
    );
};

WorkspaceBillingContent.propTypes = {
    workspace: PropTypes.shape({
        name: PropTypes.string,
        planName: PropTypes.string,
        seatsUsed: PropTypes.number,
        seatsTotal: PropTypes.number,
        pricePerSeat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        billingCycle: PropTypes.string,
    }),
    onManageBilling: PropTypes.func.isRequired,
    onUpdateSeats: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    i18nModule: PropTypes.object.isRequired,
};

export { WorkspaceBillingContent };
export default WorkspaceBillingContent;
